// Stable mapping from an asset_type_code to one of the design-system chart
// color tokens (--chart-1 .. --chart-5). These tokens are defined for both
// light and dark themes in src/index.css, so the derived accent color holds up
// in either theme with zero hardcoded colors — a requirement of the graph spec
// (asset_types has no color column today, so the color is derived, not stored).

const CHART_TOKEN_COUNT = 5

// Deterministic 32-bit FNV-1a hash of a string. Used so that the same asset
// type always maps to the same accent token across reloads and sessions.
function hashString(input: string): number {
    let hash = 0x811c9dc5
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i)
        // hash *= 16777619, kept in 32-bit range via Math.imul.
        hash = Math.imul(hash, 0x01000193)
    }
    // Coerce to an unsigned 32-bit integer.
    return hash >>> 0
}

// chartTokenIndex returns a stable index in [1, CHART_TOKEN_COUNT] for a code.
export function chartTokenIndex(assetTypeCode: string | null | undefined): number {
    const code = (assetTypeCode || 'unknown').toLowerCase()
    return (hashString(code) % CHART_TOKEN_COUNT) + 1
}

// assetTypeColorVar returns a CSS `var(--chart-N)` expression for the accent
// color of an asset type. Feed it straight into inline `style` (borderColor,
// color, backgroundColor, …).
export function assetTypeColorVar(assetTypeCode: string | null | undefined): string {
    return `var(--chart-${chartTokenIndex(assetTypeCode)})`
}

// Per-kind accent for the non-asset node kinds (§4). Each maps to a distinct
// chart token so documents/interventions/expenses/contracts/contacts read as
// different families at a glance, in both light and dark themes.
const KIND_TOKEN: Record<string, number> = {
    document: 2,
    intervention: 3,
    expense: 4,
    contact: 5,
    contract: 1,
}

// kindColorVar returns the accent `var(--chart-N)` for a non-asset entity kind.
export function kindColorVar(kind: string): string {
    return `var(--chart-${KIND_TOKEN[kind] ?? 1})`
}
